"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useLocale, useTranslations } from "next-intl";
import { differenceInMonths, format } from "date-fns";
import { ar } from "date-fns/locale/ar";
import { useGetExperiences } from "@/hooks/main-hooks";

gsap.registerPlugin(ScrollTrigger);

interface ExperienceItem {
  _id: string;
  company: string;
  role: string;
  role_ar?: string;
  description: string;
  description_ar?: string;
  employmentType: string;
  startDate: string;
  endDate: string | null;
  ongoing: boolean;
}

/** Experience — a ruled timeline; a heat line fills down the start edge as you read. */
export default function Experience() {
  const t = useTranslations("brand.about.experience");
  const locale = useLocale();
  const rtl = locale === "ar";
  const { data } = useGetExperiences();
  const experiences: ExperienceItem[] = data?.experiences || [];
  const root = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!root.current || !experiences.length) return;
    const mm = gsap.matchMedia(root);
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.from(".ex-line", {
        yPercent: 110,
        duration: 1.2,
        stagger: 0.08,
        ease: "expo.out",
        scrollTrigger: { trigger: ".ex-head", start: "top 80%", once: true },
      });
      gsap.fromTo(
        ".ex-progress",
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: "none",
          scrollTrigger: { trigger: ".ex-list", start: "top 70%", end: "bottom 60%", scrub: true },
        },
      );
      gsap.utils.toArray<HTMLElement>(".ex-row").forEach((row) => {
        gsap.from(row, {
          opacity: 0,
          y: 40,
          duration: 1,
          ease: "expo.out",
          scrollTrigger: { trigger: row, start: "top 88%", once: true },
        });
      });
    });
    return () => mm.revert();
  }, [experiences.length]);

  if (!experiences.length) return null;

  const fmt = (d: string) => format(new Date(d), "MMM yyyy", rtl ? { locale: ar } : undefined);

  const duration = (start: string, end: string | null) => {
    const months = Math.max(1, differenceInMonths(end ? new Date(end) : new Date(), new Date(start)));
    const y = Math.floor(months / 12);
    const m = months % 12;
    const parts: string[] = [];
    if (y) parts.push(t("years", { count: y }));
    if (m) parts.push(t("months", { count: m }));
    return parts.join(" · ");
  };

  return (
    <section ref={root} className="relative px-5 py-28 md:px-10 md:py-40">
      <div className="mx-auto max-w-[1400px]">
        <div className="ex-head mb-16 grid gap-8 md:mb-24 md:grid-cols-[1fr_minmax(0,420px)] md:items-end">
          <div>
            <p className="label mb-6">{t("label")}</p>
            <h2 className="display text-[clamp(2.6rem,7vw,7rem)] text-bone">
              <span className="block overflow-hidden pb-[0.04em]">
                <span className="ex-line block">{t("title1")}</span>
              </span>
              <span className="block overflow-hidden pb-[0.08em]">
                <span className="ex-line block text-ash">{t("title2")}</span>
              </span>
            </h2>
          </div>
          <p className="text-lg leading-relaxed text-ash">{t("sub")}</p>
        </div>

        <div className="ex-list relative ps-6 md:ps-10">
          {/* rail + scrubbed fill */}
          <span className="absolute inset-y-0 start-0 w-px bg-[var(--hair)]" aria-hidden />
          <span className="ex-progress absolute inset-y-0 start-0 w-px origin-top bg-heat shadow-[0_0_12px_var(--heat)]" aria-hidden />

          <ol className="border-t border-[var(--hair)]">
            {experiences.map((exp, i) => {
              const role = rtl && exp.role_ar ? exp.role_ar : exp.role;
              const desc = rtl && exp.description_ar ? exp.description_ar : exp.description;
              const current = exp.ongoing || !exp.endDate;
              return (
                <li
                  key={exp._id || i}
                  className="ex-row group relative grid gap-x-10 gap-y-5 border-b border-[var(--hair)] py-10 transition-colors duration-500 hover:bg-void-2/40 md:grid-cols-[200px_1fr] md:py-14 md:pe-6 lg:grid-cols-[220px_1fr_auto]"
                >
                  {/* node on the rail */}
                  <span
                    aria-hidden
                    className={`absolute -start-[calc(1.5rem+3.5px)] top-[2.85rem] h-2 w-2 rounded-full md:-start-[calc(2.5rem+3.5px)] md:top-[3.85rem] ${
                      current ? "bg-heat shadow-[0_0_14px_var(--heat)]" : "bg-smoke"
                    }`}
                  />

                  {/* when */}
                  <div className="flex flex-col gap-1.5">
                    <span className="tabular font-mono text-[13px] text-heat">
                      {exp.startDate ? fmt(exp.startDate) : ""} — {exp.endDate ? fmt(exp.endDate) : t("present")}
                    </span>
                    {exp.startDate && (
                      <span className="tabular font-mono text-[12px] text-smoke">{duration(exp.startDate, exp.endDate)}</span>
                    )}
                  </div>

                  {/* what */}
                  <div className="min-w-0">
                    <h3 className="font-display text-[clamp(1.6rem,3vw,2.75rem)] font-bold leading-tight tracking-[-0.03em] text-bone">
                      {role}
                    </h3>
                    <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-ash">
                      <span className="text-bone/85">{exp.company}</span>
                      {exp.employmentType && (
                        <>
                          <span className="h-1 w-1 rounded-full bg-smoke" aria-hidden />
                          <span className="label">{exp.employmentType}</span>
                        </>
                      )}
                    </p>
                    {desc && <p className="mt-5 max-w-[60ch] leading-relaxed text-ash">{desc}</p>}
                  </div>

                  {/* status / year */}
                  <div className="flex items-start lg:justify-end">
                    {current ? (
                      <span className="inline-flex items-center gap-2 rounded-full border border-[var(--hair-strong)] px-3 py-1.5 text-[13px] text-bone">
                        <span className="live-dot" />
                        {t("current")}
                      </span>
                    ) : (
                      <span className="display latin tabular text-[clamp(2rem,3.4vw,3rem)] text-smoke transition-colors duration-500 group-hover:text-bone" dir="ltr">
                        {exp.startDate ? new Date(exp.startDate).getFullYear() : ""}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
